import { getCityFooterData } from "@components/common/SEO/utils/getCityFooterData";
import {
  MeContextBasicQuery,
  ProductType,
  TourOrderBy,
  TourStatus,
} from "@graphql/types";
import { getCurrencyConversionAmount } from "@helpers/getCurrencyConversion";
import { graphql } from "@helpers/graphql";
import { isInValidSlug } from "@helpers/isInValidSlug";
import { GetStaticPropsResult } from "next";

interface LoaderParams {
  slug: string;
  country: string;
  auth?: MeContextBasicQuery;
}

interface Props {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export const loader_tourBySlug = async ({
  auth,
  ...params
}: LoaderParams): Promise<GetStaticPropsResult<Props>> => {
  const tourSlug = params.slug;

  if (isInValidSlug(tourSlug)) {
    return { notFound: true };
  }

  const tourId = Number(String(tourSlug).split("-").pop());

  if (isNaN(tourId)) {
    return {
      redirect: {
        destination: params?.country ? `/${String(params?.country)}` : "/",
        statusCode: 301,
      },
    };
  }

  const tour = await graphql.TourByIdWithVideoThumbnail({ id: tourId });
  const supplierId = tour.tourById?.supplier.id || 0;
  const categoryId = tour.tourById?.category?.id || 0;
  const cityId = tour.tourById?.city?.id || 0;

  const reviews = await graphql.reviews(getReviewsVariables(supplierId));
  const supplierTours = await graphql.tours(
    getSupplierToursVariables(supplierId, [tourId])
  );
  const popularTours = await graphql.tours(
    getPopularToursVariables(
      categoryId,
      cityId,
      tour.tourById?.productType,
      tourId
    )
  );

  let tourPrice = 0;
  if (tour.tourById?.price) {
    tourPrice = getCurrencyConversionAmount(
      tour.tourById?.price.amount,
      tour.tourById?.price.currency
    );
  }

  if ((tour && tour.tourById == null) || !tour.tourById) {
    return {
      redirect: {
        destination: params?.country ? `/${String(params?.country)}` : "/",
        statusCode: 302,
      },
    };
  }

  const isPublished = tour.tourById?.status === TourStatus.Published;
  const isActive = tour.tourById?.isActive;

  if (isPublished === false || (isPublished && isActive === false)) {
    if (!auth) {
      return {
        redirect: {
          destination: `/${String(params?.country)}`,
          statusCode: 307,
        },
      };
    }

    const isAdmin = auth.me.isAdmin;
    const isOwner = auth.me.id === tour.tourById?.supplier.id;

    if (!isAdmin && !isOwner) {
      return {
        redirect: {
          destination: `/${String(params?.country)}`,
          statusCode: 307,
        },
      };
    }
  }

  return {
    props: {
      initialData: {
        tour,
        reviews,
        supplierTours,
        popularTours,
        tourPrice,
        footer: await getCityFooterData(tour.tourById.city),
      },
    },
  };
};

export const getReviewsVariables = (supplierId: number) => {
  return {
    first: 3,
    filters: {
      SUPPLIERS_IDS: [supplierId],
    },
  };
};

export const getSupplierToursVariables = (
  supplierId: number,
  tourIds?: [number]
) => {
  return {
    first: 4,
    filters: {
      SUPPLIERS_IDS: [supplierId],
      ORDER_BY: TourOrderBy.Recommended,
      ...(tourIds !== undefined &&
        tourIds.length > 0 && { EXCLUDE_IDS: tourIds }),
    },
  };
};

export const getPopularToursVariables = (
  categoryId: number,
  cityId: number,
  productType?: ProductType,
  tourId?: number
) => {
  return {
    first: 4,
    filters: {
      CITIES_IDS: [cityId],
      CATEGORIES_IDS: [categoryId],
      ORDER_BY: TourOrderBy.Recommended,
      ...(!!tourId && { EXCLUDE_IDS: [tourId] }),
      ...(!!productType && { PRODUCT_TYPES: [productType] }),
    },
  };
};
